import { useMemo, useState } from "react";
import { Calculator } from "lucide-react";

function Row({ label, value, suffix, min, max, step, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span className="text-[var(--slate)]">{label}</span>
        <span className="text-[#0A1128] font-medium">{value}{suffix}</span>
      </div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--gold)] cursor-pointer" />
    </div>
  );
}

export default function MortgageCalculator({ price = 0, currency = "US$" }) {
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(25);

  const { monthly, loan } = useMemo(() => {
    const loanAmt = price * (1 - down / 100);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r === 0 ? loanAmt / n : (loanAmt * r) / (1 - Math.pow(1 + r, -n));
    return { monthly: isFinite(m) ? m : 0, loan: loanAmt };
  }, [price, down, rate, years]);

  const fmt = (n) => `${currency}${Math.round(n).toLocaleString("en-US")}`;

  return (
    <div data-testid="mortgage-calculator" className="border border-[#0A1128]/12 bg-white p-8 mt-14">
      <div className="flex items-center gap-3 mb-6">
        <Calculator size={18} className="text-[var(--gold)]" />
        <h2 className="font-serif text-2xl text-[#0A1128]">Mortgage Estimator</h2>
      </div>
      <div className="space-y-6">
        <Row label="Down Payment" value={down} suffix="%" min={5} max={60} step={5} onChange={setDown} testid="mc-down" />
        <Row label="Interest Rate" value={rate} suffix="%" min={2} max={12} step={0.25} onChange={setRate} testid="mc-rate" />
        <Row label="Term" value={years} suffix=" yrs" min={10} max={30} step={5} onChange={setYears} testid="mc-years" />
      </div>
      <div className="mt-8 pt-6 border-t border-[#0A1128]/10 grid grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Loan Amount</div>
          <div className="font-serif text-2xl text-[#0A1128] mt-1">{fmt(loan)}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Est. Monthly</div>
          <div data-testid="mc-monthly" className="font-serif text-2xl text-[var(--gold)] mt-1">{fmt(monthly)}</div>
        </div>
      </div>
      <p className="text-xs text-[var(--slate)]/70 mt-5 leading-relaxed">Estimate excludes insurance, stamp duty, and fees. For illustration only — contact me for tailored financing guidance.</p>
    </div>
  );
}
